import React, { useEffect, useState } from "react";
import { Check, Copy, MessageSquareHeart, QrCode, X } from "lucide-react";
import api from "../../lib/api";
import { copyPlainText } from "../../lib/clipboard";
import { dayLabel } from "../../lib/initialAssessment";
import FeedbackResults from "../FeedbackResults";

/**
 * The trainer's "Feedback de final" (30.10.2026), on the group's page: the
 * group link to show at the last session, its QR code and how many answered;
 * after closing, the results, from 3 answers up (decision F).
 */
export default function FeedbackCard({ groupId }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/portal/groups/${groupId}/final-feedback`).then((r) => { if (alive) setData(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [groupId]);

  if (!data?.applies) return null;
  const results = data.results || {};
  if (!data.card && !results.shown && !(results.closed && results.answered > 0) && !(data.testimonials || []).length) return null;

  const copy = async () => {
    if (await copyPlainText(data.url)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section className="mt-8 rounded-2xl border border-[#965EA7]/25 bg-white p-5 sm:p-6" data-testid="portal-feedback-card">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#373A45]">
        <MessageSquareHeart size={18} className="text-[#965EA7]" /> Feedback de final
      </h2>
      {data.card && (
        <>
          <p className="mt-2 text-sm leading-relaxed text-[#373A45]">
            La ultima sesiune, arată cursanților linkul sau codul QR și lasă-le 5 minute să răspundă. Cine e intrat în portal
            completează direct, {data.mode === "automat"
              ? "iar cine nu răspunde atunci primește chestionarul de feedback pe email după sesiune."
              : "iar cine nu răspunde atunci nu primește email automat."}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <code className="rounded-lg bg-[#F5F6F6] px-3 py-2 text-sm text-[#373A45] [overflow-wrap:anywhere]" data-testid="portal-feedback-url">
              {data.url.replace(/^https?:\/\//, "")}
            </code>
            <button type="button" onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-sm font-semibold text-[#7A4986] hover:bg-[#965EA7]/10">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copiat" : "Copiază linkul"}
            </button>
            {data.qr && (
              <button type="button" onClick={() => setQr(true)} data-testid="portal-feedback-qr-open"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-sm font-semibold text-[#7A4986] hover:bg-[#965EA7]/10">
                <QrCode size={14} /> Arată codul QR
              </button>
            )}
          </div>
          <p className="mt-3 text-sm text-[#373A45]" data-testid="portal-feedback-count">
            <strong>{data.answered} din {data.people}</strong> cursanți au răspuns.
            {data.deadline && <span className="text-[#6B7280]"> Se închide {dayLabel(data.deadline)}.</span>}
          </p>
        </>
      )}
      {!data.card && results.shown && (
        <p className="mt-2 text-sm text-[#6B7280]">Chestionarul de feedback s-a închis {dayLabel(data.deadline)}.</p>
      )}
      {!data.card && !results.shown && results.closed && (
        <p className="mt-2 text-sm text-[#6B7280]" data-testid="portal-feedback-too-few">
          Au răspuns doar {results.answered} cursanți, așa că nu arătăm rezultatele: s-ar putea ghici cine ce a spus.
        </p>
      )}
      {results.shown && <div className="mt-5"><FeedbackResults results={results} people={data.people} /></div>}
      {(data.testimonials || []).length > 0 && (
        <div className="mt-5" data-testid="portal-feedback-testimonials">
          <p className="text-sm font-semibold text-[#373A45]">Ce au spus cursanții tăi pe site</p>
          <ul className="mt-2 grid gap-2">
            {data.testimonials.map((t) => (
              <li key={t.id} className="rounded-xl bg-[#F5F6F6] px-4 py-2.5 text-sm text-[#373A45]">
                {t.text}
                <span className="mt-1 block text-xs text-[#6B7280]">{[t.name, [t.role, t.city].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {qr && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" role="dialog" aria-modal="true"
          aria-label="Codul QR al chestionarului de feedback" onClick={() => setQr(false)}>
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setQr(false)} aria-label="Închide"
              className="absolute right-4 top-4 rounded-full p-1 text-[#6B7280] hover:bg-[#F5F6F6]"><X size={18} /></button>
            <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(data.qr)}`} alt="Cod QR"
              className="mx-auto w-full max-w-sm" data-testid="portal-feedback-qr" />
            <p className="mt-3 font-display text-xl font-bold text-[#373A45]">{data.url.replace(/^https?:\/\//, "")}</p>
          </div>
        </div>
      )}
    </section>
  );
}
